import { DndContext, DragOverlay, KeyboardSensor, MeasuringStrategy, MouseSensor, TouchSensor, useSensor, useSensors, type UniqueIdentifier } from '@dnd-kit/core'
import { useCallback, useMemo } from 'react'
import { toast } from '../components/toasts'
import { useBoardState, useStore } from '../store/context'
import { stateName } from '../vocab'
import { CardBody } from './Card'
import { attachmentCount, recordFactsOf } from './cardFacts'
import { Column } from './Column'
import { boardKeyboardCoordinates, useBoardDnd, type Drop } from './dnd'
import type { ColumnData } from './filters'
import { sortKeyForDrop } from './sortKey'
import type { SortMode } from './view'

const measuring = { droppable: { strategy: MeasuringStrategy.Always } }
export function BoardColumns({ columns, sort, onToggleShowAll, onExpandClosed }: {
  columns: ColumnData[]; sort: SortMode; onToggleShowAll: (key: string) => void; onExpandClosed: () => void
}) {
  const store = useStore()
  const { issues, labels, me, entries, attachments } = useBoardState()
  const base = useMemo(() => Object.fromEntries(columns.map(c => [c.key, c.items.map(i => i.id)])), [columns])
  const describe = useCallback((id: UniqueIdentifier) => store.getState().issues.get(String(id))?.key ?? 'the issue', [store])
  const nameOf = useCallback((key: string) => {
    const found = columns.find(c => c.key === key)
    return found ? found.column?.name ?? stateName(found.state) : key
  }, [columns])
  const onDrop = useCallback(({ issueId, from, to, orderedIds }: Drop) => {
    if (from === to && sort !== 'manual') return
    const target = columns.find(c => c.key === to)
    if (!target) return
    const all = store.getState().issues
    const key = sortKeyForDrop({ issueId, place: to, orderedIds, mode: sort, issues: all })
    if (key === null && sort === 'manual') {
      toast(`${nameOf(to)} has sort keys the board cannot place a card between.`, 'error')
      return
    }
    void store.moveIssue(issueId, target.state, key ?? undefined, target.column?.id ?? null)
  }, [store, sort, columns, nameOf])
  const dnd = useBoardDnd(base, onDrop, describe, nameOf)
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 220, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: boardKeyboardCoordinates,
      keyboardCodes: { start: ['F2'], cancel: ['Escape'], end: ['Space', 'Enter'] } }),
  )
  const active = dnd.activeId ? issues.get(dnd.activeId) : undefined
  return <DndContext sensors={sensors} collisionDetection={dnd.collisionDetection} measuring={measuring}
    onDragStart={dnd.onDragStart} onDragOver={dnd.onDragOver} onDragEnd={dnd.onDragEnd} onDragCancel={dnd.onDragCancel}
    accessibility={{ announcements: dnd.announcements, screenReaderInstructions: {
      draggable: 'To move this issue, press F2, then arrows; Space or Enter drops it, Escape cancels.',
    } }}>
    <div className="columns">{columns.map(column => <Column key={column.key} column={column} ids={dnd.items[column.key] ?? []}
      manual={sort === 'manual'} issues={issues} onToggleShowAll={onToggleShowAll} onExpand={onExpandClosed} />)}</div>
    <DragOverlay dropAnimation={{ duration: 160, easing: 'cubic-bezier(0.2, 0, 0, 1)' }}>
      {/* The dragged card keeps its looks: its markers. data-priority marks it a board card for the CSS. */}
      {active && <div className="card card-overlay" data-priority={active.priority}>
        <CardBody issue={active} labels={labels} me={me}
          counts={{ comments: recordFactsOf(entries, active.id).comments, attachments: attachmentCount(attachments, active.id) }} /></div>}
    </DragOverlay>
  </DndContext>
}
